import {
  CompareArrows as CompareArrowsIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Box, ButtonGroup } from '@mui/material';
import AdminUser from '../../models/admin/AdminUser';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { ContentReviewInterface } from '../../models/ContentReviewInterface';
import { ReviewRequest } from '../../models/ReviewRequest';
import React from 'react';
import { StyledButton } from '../styles/StyledButtons';
import { Palette } from 'Palette';
import ReviewContentComparisonView from './ReviewContentComparisonView';
import ReviewContentEditView from './ReviewContentEditView';
import ReviewContentReadView from './ReviewContentReadView';
import {
  canRequesterAddContent,
  getContentTypeFromReviewType,
  getDefaultComparisonMode,
} from './reviewViewerUtils';
import { ReviewViewMode } from './useReviewViewer';

interface ReviewViewerContentViewsProps {
  tone: keyof typeof Palette;
  viewMode: ReviewViewMode;
  onViewModeChange: (mode: ReviewViewMode) => void;
  publishedContent: ContentManagementInterface;
  draftContent: ContentManagementInterface;
  reviewData: ContentReviewInterface;
  reviewRequest: ReviewRequest;
  currentUser: AdminUser;
  onReviewDataUpdate: (updatedReviewData: ContentReviewInterface) => Promise<void>;
  onSaveDraft: () => Promise<void>;
}

export default function ReviewViewerContentViews({
  tone,
  viewMode,
  onViewModeChange,
  publishedContent,
  draftContent,
  reviewData,
  reviewRequest,
  currentUser,
  onReviewDataUpdate,
  onSaveDraft,
}: ReviewViewerContentViewsProps) {
  const modelType = getContentTypeFromReviewType(reviewRequest.type);

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <ButtonGroup variant="outlined" fullWidth>
          <StyledButton
            startIcon={<VisibilityIcon />}
            onClick={() => onViewModeChange('read')}
            variant={viewMode === 'read' ? 'contained' : 'outlined'}
          >
            Read View
          </StyledButton>
          <StyledButton
            startIcon={<EditIcon />}
            onClick={() => onViewModeChange('edit')}
            variant={viewMode === 'edit' ? 'contained' : 'outlined'}
          >
            Edit Mode
          </StyledButton>
          <StyledButton
            startIcon={<CompareArrowsIcon />}
            onClick={() => onViewModeChange('comparison')}
            variant={viewMode === 'comparison' ? 'contained' : 'outlined'}
          >
            Compare View
          </StyledButton>
        </ButtonGroup>
      </Box>

      {viewMode === 'read' && (
        <ReviewContentReadView
          publishedContent={publishedContent}
          reviewData={reviewData}
          modelType={modelType}
          tone={tone}
        />
      )}

      {viewMode === 'edit' && (
        <ReviewContentEditView
          publishedContent={publishedContent}
          draftContent={draftContent}
          reviewData={reviewData}
          currentUser={currentUser}
          onReviewDataUpdate={onReviewDataUpdate}
          modelType={modelType}
          tone={tone}
          canAddContent={canRequesterAddContent(reviewRequest, currentUser)}
        />
      )}

      {viewMode === 'comparison' && (
        <ReviewContentComparisonView
          publishedContent={publishedContent}
          draftContent={draftContent}
          reviewData={reviewData}
          currentUser={currentUser}
          requestedBy={reviewRequest.requestedBy.id}
          requestedOf={reviewRequest.requestedOf.id}
          publishingPermission={true}
          tone={tone}
          defaultComparisonMode={getDefaultComparisonMode(reviewRequest, currentUser)}
          onReviewDataUpdate={onReviewDataUpdate}
          onSaveDraft={onSaveDraft}
          onRequestReview={() => undefined}
          showActionButtons={false}
        />
      )}
    </>
  );
}
